import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Container, Stack, Typography, Grid } from '@mui/material';
import { Box } from '@mui/system';
// material icons
import UpdateIcon from '@mui/icons-material/Update';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import PersonIcon from '@mui/icons-material/Person';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import BadgeIcon from '@mui/icons-material/Badge';
import MapIcon from '@mui/icons-material/Map';
import PlaceIcon from '@mui/icons-material/Place';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HomeIcon from '@mui/icons-material/Home';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import PublicIcon from '@mui/icons-material/Public';

// components
import { useDispatch } from 'react-redux';
import { normalizeValue, truncateText } from '../../utils/CommonFunction';
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import Scrollbar from '../../components/Scrollbar';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { getCustomerView } from '../../store/Slice/Customer/CustomerSlice';
import useCustomerSelector from '../../store/Slice/Customer/CustomerSelector';
import { NOT_AVAILABLE } from '../Global/Constants';

// ----------------------------------------------------------------------

const DETAILS_CONFIG = [
  { label: 'Address', key: 'address', icon: HomeIcon },
  { label: 'City', key: 'cityName', icon: LocationCityIcon },
  { label: 'Country', key: 'countryId', icon: PublicIcon },
  { label: 'Created On', key: 'createdOn', icon: CalendarMonthIcon },
  { label: 'Updated On', key: 'updatedOn', icon: UpdateIcon },
  { label: 'Customer ID', key: 'customerId', icon: FingerprintIcon, truncate: 15 },
  { label: 'First Name', key: 'customerFirstName', icon: PersonIcon, truncate: 15 },
  { label: 'Middle Name', key: 'customerMiddleName', icon: PersonOutlineIcon, truncate: 15 },
  { label: 'Last Name', key: 'customerLastName', icon: PersonOutlineIcon, truncate: 15 },
  { label: 'Customer Name', key: 'customerName', icon: BadgeIcon, truncate: 15 },
  { label: 'District', key: 'districtId', icon: MapIcon, truncate: 15 },
  { label: 'Province', key: 'provinceId', icon: PlaceIcon, truncate: 15 },
  { label: 'Email', key: 'email', icon: EmailIcon, truncate: 15 },
  { label: 'Mobile', key: 'mobile', icon: PhoneIcon },
  { label: 'Loan Type', key: 'loanType', icon: RequestQuoteIcon },
  {
    label: 'Status',
    icon: InfoIcon,
    customValue: (data) => (
      <>
        {normalizeValue(data?.status, NOT_AVAILABLE)}
        {normalizeValue(data?.isActive, NOT_AVAILABLE)}
      </>
    )
  },
  {
    label: 'Is Active',
    icon: CheckCircleIcon,
    customValue: (data) =>
      data?.isActive !== undefined ? (data.isActive ? 'Yes' : 'No') : NOT_AVAILABLE
  }
];

function CustomerView() {
  const { CustomerViewData } = useCustomerSelector();
  const location = useLocation();
  const rowId = location.state || null;

  const { LoginData } = useLoginSelector();
  const dispatch = useDispatch();

  const getCustomerViewApi = useCallback(() => {
    dispatch(
      getCustomerView({
        customerId: rowId,
        userId: LoginData?.userId
      })
    );
  }, [LoginData?.userId, rowId, dispatch]);

  useEffect(() => {
    getCustomerViewApi();
  }, [getCustomerViewApi]);

  const renderDetailItem = useCallback(
    (item) => {
      const { label, key, icon: Icon, truncate, customValue } = item;
      let displayValue = '';

      if (customValue) {
        displayValue = customValue(CustomerViewData);
      } else {
        displayValue = normalizeValue(CustomerViewData?.[key], NOT_AVAILABLE);
        if (truncate && displayValue !== NOT_AVAILABLE) {
          displayValue = truncateText(displayValue, truncate);
        }
      }

      return (
        <Grid item xs={4} key={label}>
          <Box className="displayFlex">
            <Icon className="viewLabelIcon" />
            <Box>
              <Typography variant="body2" className="labelText">
                {label}
              </Typography>
              <Typography variant="subtitle2" className="labelValue">
                {displayValue}
              </Typography>
            </Box>
          </Box>
        </Grid>
      );
    },
    [CustomerViewData]
  );

  return (
    <Page title="Kpros by Kalolytic Solutions || Customer">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="View Customer" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>
          <Scrollbar>
            <Typography variant="body1" className="headingLineUp headingLineUpOut">
              Customer details
            </Typography>
            <Box className="viewLeadCard cardTopMinusMargin" mb={3}>
              <Grid container spacing={3}>
                {DETAILS_CONFIG.map(renderDetailItem)}
              </Grid>
            </Box>
          </Scrollbar>
        </Box>
      </Container>
    </Page>
  );
}

export default CustomerView;
